import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { cn } from '../lib/cn'

/**
 * Apple-style liquid glass, after aave.com/design/building-glass-for-the-web,
 * ported from the working implementation in github.com/shuding/liquid-glass.
 *
 * A displacement map is generated on a canvas: for every pixel we compute
 * where the lens should sample the backdrop from (identity in the center,
 * pulling toward the inside along a band at the rim, which magnifies the
 * backdrop at the edges exactly like glass). The offsets are normalized to
 * the largest one, encoded into R/G channels, and fed to an SVG
 * `feDisplacementMap` whose `scale` matches the normalization, applied via
 * `backdrop-filter: url(#...)`.
 *
 * The glass itself is nearly clear — blur(0.25px) contrast brightness
 * saturate — the effect IS the refraction. Chromium only; Safari/Firefox
 * can't run url() filters inside backdrop-filter and fall back to frosted
 * blur. Drop it as the first child of any `relative` pill container.
 */

const BAND = 18 // px width of the refracting rim band
const DEPTH = 14 // how far the rim bends the backdrop, px

function smoothStep(a: number, b: number, t: number) {
  t = Math.max(0, Math.min(1, (t - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

/** signed distance to a rounded-rect border, centered at origin (<0 inside) */
function sdRoundedBox(x: number, y: number, hw: number, hh: number, r: number) {
  const qx = Math.abs(x) - hw + r
  const qy = Math.abs(y) - hh + r
  return (
    Math.min(Math.max(qx, qy), 0) +
    Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) -
    r
  )
}

type DisplacementMap = { uri: string; w: number; h: number; scale: number }

function makeDisplacementMap(w: number, h: number, radius: number): DisplacementMap {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  const data = new Uint8ClampedArray(w * h * 4)
  const raw = new Float32Array(w * h * 2)

  let maxD = 0
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const cx = x - w / 2 + 0.5
      const cy = y - h / 2 + 0.5
      const sd = sdRoundedBox(cx, cy, w / 2, h / 2, radius)
      // 1 at the rim → 0 once BAND px inside
      const t = smoothStep(BAND, 0, -sd)
      let dx = 0
      let dy = 0
      if (t > 0) {
        // outward normal from the SDF gradient
        let nx = sdRoundedBox(cx + 1, cy, w / 2, h / 2, radius) - sdRoundedBox(cx - 1, cy, w / 2, h / 2, radius)
        let ny = sdRoundedBox(cx, cy + 1, w / 2, h / 2, radius) - sdRoundedBox(cx, cy - 1, w / 2, h / 2, radius)
        const len = Math.hypot(nx, ny) || 1
        nx /= len
        ny /= len
        // sample toward the inside, hardest at the rim → edge magnification
        const m = t * t * DEPTH
        dx = -nx * m
        dy = -ny * m
        maxD = Math.max(maxD, Math.abs(dx), Math.abs(dy))
      }
      const i = (y * w + x) * 2
      raw[i] = dx
      raw[i + 1] = dy
    }
  }

  maxD = Math.max(maxD, 1e-6)
  for (let p = 0; p < w * h; p++) {
    const i = p * 4
    data[i] = ((raw[p * 2] / maxD) * 0.5 + 0.5) * 255 // R: horizontal
    data[i + 1] = ((raw[p * 2 + 1] / maxD) * 0.5 + 0.5) * 255 // G: vertical
    data[i + 2] = 0
    data[i + 3] = 255
  }

  ctx.putImageData(new ImageData(data, w, h), 0, 0)
  // offset = scale * (channel - 0.5); channel spans ±0.5 → scale = 2·maxD
  return { uri: canvas.toDataURL(), w, h, scale: 2 * maxD }
}

export function GlassSurface({
  className,
  tintOpacity = 0.18,
}: {
  className?: string
  /** Opacity of the white tint behind the refraction. Raise this where the
   *  glass sits over busy/dark backdrops (e.g. the nav pill over a testimonial
   *  card) and the default reads as too transparent for legible content on top. */
  tintOpacity?: number
}) {
  const rawId = useId()
  const filterId = useMemo(() => 'glass' + rawId.replace(/\W/g, ''), [rawId])
  const ref = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<DisplacementMap | null>(null)
  const [allowDisplacement, setAllowDisplacement] = useState(false)

  // url() inside backdrop-filter only works on Chromium
  const chromium = useMemo(
    () => typeof navigator !== 'undefined' && /Chrome/.test(navigator.userAgent),
    [],
  )

  useEffect(() => {
    if (!chromium) return
    const finePointer = window.matchMedia('(pointer: fine)')
    const roomy = window.matchMedia('(min-width: 768px)')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () =>
      setAllowDisplacement(finePointer.matches && roomy.matches && !reduced.matches)
    update()
    finePointer.addEventListener('change', update)
    roomy.addEventListener('change', update)
    reduced.addEventListener('change', update)
    return () => {
      finePointer.removeEventListener('change', update)
      roomy.removeEventListener('change', update)
      reduced.removeEventListener('change', update)
    }
  }, [chromium])

  useEffect(() => {
    if (!allowDisplacement) {
      setMap(null)
      return
    }
    const el = ref.current
    if (!el) return
    let raf = 0
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const w = Math.round(el.offsetWidth)
        const h = Math.round(el.offsetHeight)
        if (w > 0 && h > 0) setMap(makeDisplacementMap(w, h, h / 2))
      })
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [allowDisplacement])

  // clear refractive glass on Chromium; frosted fallback elsewhere
  const filter = map
    ? `url(#${filterId}) blur(0.25px) contrast(1.15) brightness(1.05) saturate(1.1)`
    : 'blur(12px) saturate(1.6)'

  return (
    <div ref={ref} className={cn('absolute inset-0 rounded-full', className)}>
      {map && (
        <svg aria-hidden width="0" height="0" className="absolute">
          <defs>
            <filter
              id={filterId}
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
              x="0"
              y="0"
              width={map.w}
              height={map.h}
            >
              <feImage
                href={map.uri}
                xlinkHref={map.uri}
                x="0"
                y="0"
                width={map.w}
                height={map.h}
                preserveAspectRatio="none"
                result="map"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="map"
                scale={map.scale}
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </defs>
        </svg>
      )}

      {/* refraction + tint */}
      <div
        className="absolute inset-0 overflow-hidden rounded-full"
        style={{
          backdropFilter: filter,
          WebkitBackdropFilter: filter,
          background: `rgba(255, 255, 255, ${tintOpacity})`,
        }}
      />

      {/* specular rim + drop shadow */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow: [
            'inset 0 0 0 1px rgba(255,255,255,0.3)', // hairline ring
            'inset 1.8px 3px 0 -2px rgba(255,255,255,0.85)', // top-left catch
            'inset -2px -2px 0 -2px rgba(255,255,255,0.6)', // bottom-right catch
            '0 10px 28px -10px rgba(20,20,23,0.2)', // soft drop
            '0 2px 6px rgba(20,20,23,0.05)',
          ].join(', '),
        }}
      />
    </div>
  )
}
